import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor, useEditorState, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useId, useState } from "react";

import { Button } from "../components/Button";
import { Icon, type IconName } from "../components/Icon";
import { Input } from "../components/Input";
import { cn } from "../lib/cn";

export interface RichTextEditorLabels {
  toolbar: string;
  bold: string;
  italic: string;
  underline: string;
  strike: string;
  heading2: string;
  heading3: string;
  bulletList: string;
  orderedList: string;
  quote: string;
  link: string;
  unlink: string;
  undo: string;
  redo: string;
  linkUrl: string;
  applyLink: string;
  cancel: string;
}

export interface RichTextEditorProps {
  /** HTML. */
  value: string;
  onChange: (html: string) => void;
  labels: RichTextEditorLabels;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Accessible name of the editing area (usually the field label). */
  "aria-label": string;
  className?: string;
}

type Mark = "bold" | "italic" | "underline" | "strike";

/** An empty document is "" (not "<p></p>"), so required checks and dirty tracking behave. */
function html(editor: Editor): string {
  return editor.isEmpty ? "" : editor.getHTML();
}

/**
 * Rich text for CMS pages, blog posts, FAQ answers and product descriptions (Tiptap). It produces a small set of
 * tags (paragraphs, H2/H3, bold/italic/underline/strike, lists, quotes, links); the server sanitises the HTML again.
 */
export function RichTextEditor({
  value,
  onChange,
  labels,
  placeholder,
  disabled = false,
  invalid = false,
  className,
  ...aria
}: RichTextEditorProps) {
  const [linking, setLinking] = useState(false);
  const [url, setUrl] = useState("");
  const urlId = useId();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        link: { openOnClick: false, autolink: true, protocols: ["https", "mailto", "tel"] },
      }),
      Placeholder.configure({ placeholder: placeholder ?? "" }),
    ],
    content: value,
    editable: !disabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": aria["aria-label"],
        ...(invalid ? { "aria-invalid": "true" } : {}),
        class: "min-h-40 px-3.5 py-3 outline-none",
      },
    },
    onUpdate: ({ editor: current }) => onChange(html(current)),
  });

  // keep in step when the form resets or loads another record
  useEffect(() => {
    if (editor && value !== html(editor)) editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);
  useEffect(() => {
    if (editor && editor.isEditable === disabled) editor.setEditable(!disabled, false); // not an edit
  }, [editor, disabled]);

  const state = useEditorState({
    editor,
    selector: ({ editor: current }) =>
      current
        ? {
            bold: current.isActive("bold"),
            italic: current.isActive("italic"),
            underline: current.isActive("underline"),
            strike: current.isActive("strike"),
            h2: current.isActive("heading", { level: 2 }),
            h3: current.isActive("heading", { level: 3 }),
            bullet: current.isActive("bulletList"),
            ordered: current.isActive("orderedList"),
            quote: current.isActive("blockquote"),
            link: current.isActive("link"),
            canUndo: current.can().undo(),
            canRedo: current.can().redo(),
          }
        : null,
  });

  const button = (
    icon: IconName,
    label: string,
    action: () => void,
    pressed?: boolean,
    enabled = true,
  ) => (
    <Button
      key={icon}
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={disabled || !editor || !enabled}
      className={cn(pressed && "bg-muted")}
      onMouseDown={(event) => event.preventDefault()} // keep the selection
      onClick={action}
    >
      <Icon name={icon} size={16} />
    </Button>
  );
  const chain = () => editor?.chain().focus();
  const toggle = (mark: Mark) => () => {
    const c = chain();
    if (!c) return;
    ({
      bold: () => c.toggleBold().run(),
      italic: () => c.toggleItalic().run(),
      underline: () => c.toggleUnderline().run(),
      strike: () => c.toggleStrike().run(),
    })[mark]();
  };
  const applyLink = () => {
    const href = url.trim();
    if (!editor) return;
    if (!href) editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    setLinking(false);
  };

  return (
    <div
      className={cn(
        "rounded-input border border-input bg-surface text-sm focus-within:ring-2 focus-within:ring-brand",
        invalid && "border-danger",
        disabled && "opacity-60",
        className,
      )}
    >
      <div
        role="toolbar"
        aria-label={labels.toolbar}
        className="flex flex-wrap items-center gap-0.5 border-b border-border px-1.5 py-1"
      >
        {button("bold", labels.bold, toggle("bold"), state?.bold)}
        {button("italic", labels.italic, toggle("italic"), state?.italic)}
        {button("underline", labels.underline, toggle("underline"), state?.underline)}
        {button("strikethrough", labels.strike, toggle("strike"), state?.strike)}
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        {button(
          "heading-2",
          labels.heading2,
          () => chain()?.toggleHeading({ level: 2 }).run(),
          state?.h2,
        )}
        {button(
          "heading-3",
          labels.heading3,
          () => chain()?.toggleHeading({ level: 3 }).run(),
          state?.h3,
        )}
        {button("list", labels.bulletList, () => chain()?.toggleBulletList().run(), state?.bullet)}
        {button(
          "list-ordered",
          labels.orderedList,
          () => chain()?.toggleOrderedList().run(),
          state?.ordered,
        )}
        {button("quote", labels.quote, () => chain()?.toggleBlockquote().run(), state?.quote)}
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        {button(
          "link",
          labels.link,
          () => {
            setUrl(editor?.getAttributes("link").href ?? "https://");
            setLinking(true);
          },
          state?.link,
        )}
        {button(
          "unlink",
          labels.unlink,
          () => chain()?.extendMarkRange("link").unsetLink().run(),
          false,
          Boolean(state?.link),
        )}
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        {button("undo", labels.undo, () => chain()?.undo().run(), false, Boolean(state?.canUndo))}
        {button("redo", labels.redo, () => chain()?.redo().run(), false, Boolean(state?.canRedo))}
      </div>
      {linking && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-2 py-2">
          <label htmlFor={urlId} className="sr-only">
            {labels.linkUrl}
          </label>
          <Input
            id={urlId}
            className="h-9 min-w-0 flex-1"
            value={url}
            placeholder="https://"
            onChange={(event) => setUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                applyLink();
              }
              if (event.key === "Escape") setLinking(false);
            }}
          />
          <Button type="button" size="sm" onClick={applyLink}>
            {labels.applyLink}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setLinking(false)}>
            {labels.cancel}
          </Button>
        </div>
      )}
      <EditorContent editor={editor} className="rich-text" />
    </div>
  );
}
